import { v4 as uuid } from 'uuid';
import { KEY_POINT_CALLOUT_NODE_NAME } from '../../editor/extensions/key-point-callout';
import { CalloutBlock, CalloutColor, ContentBlockType } from '../../model';
import { BlockToTiptapConverter, convertBlocksToTiptapNodes, ConverterContext } from '../blocks-to-tiptap.converter';
import { convertTiptapNodesToBlocks, TiptapToBlockConverter } from '../tiptap-to-blocks.converter';

/**
 * Converts a CalloutBlock to a Tiptap keyPointCallout node.
 * Recursively converts nested content blocks.
 *
 * @param block - The CalloutBlock to convert
 * @param context - Optional context to pass through (e.g., 'popover' to skip popover processing)
 * @returns A Tiptap JSON node representing the callout
 */
export const convertCalloutBlockToTiptap = ((block, context?: ConverterContext) => {
  return {
    type: KEY_POINT_CALLOUT_NODE_NAME,
    attrs: {
      title: block.title,
      color: block.color,
      ...(block.icon && { icon: block.icon }),
      ...(block.custom_css && { custom_css: block.custom_css }),
      ...(block.custom_style && { custom_style: block.custom_style }),
    },
    content: convertBlocksToTiptapNodes(block.content, context),
  };
}) satisfies BlockToTiptapConverter<CalloutBlock>;

/**
 * Converts a Tiptap keyPointCallout node back to a CalloutBlock.
 * Recursively converts nested content.
 *
 * @param json - The Tiptap JSON node to convert
 * @param context - Optional converter context to thread through recursive calls
 * @returns A CalloutBlock
 */
export const convertTiptapToCalloutBlock = ((json, context?) => {
  const attrs = json.attrs ?? {};
  const content = json.content ?? [];

  return {
    id: uuid(),
    type: ContentBlockType.Callout,
    title: (attrs['title'] as string) ?? '',
    color: (attrs['color'] as CalloutColor) ?? CalloutColor.Yellow,
    ...(attrs['icon'] && { icon: attrs['icon'] as string }),
    ...(attrs['custom_css'] && { custom_css: attrs['custom_css'] as string }),
    ...(attrs['custom_style'] && { custom_style: attrs['custom_style'] as Record<string, string> }),
    content: content.length ? convertTiptapNodesToBlocks(content, context) : [],
  };
}) satisfies TiptapToBlockConverter;
